/**
 * @file App provider list and patient provider display helpers.
 */

import { appData } from '../../app/state/state.js';
import {
    getSortedUsers,
    getUserFullDisplayName,
} from './userDisplay.js';

/**
 * Returns active app providers for patient/provider workflows.
 * @returns {Array<object>} Sorted provider user records.
 */
export function getAppProviderUsers() {
    const users = appData?.users || [];
    return getSortedUsers(
        users.filter((user) =>
            user?.isAppProvider === true &&
            user?.isActive !== false,
        ),
    );
}

/**
 * Resolves a patient provider ID to a display label.
 * @param {string} providerValue Stored provider user ID.
 * @returns {string} Display label.
 */
export function getPatientProviderDisplayName(providerValue) {
    if (!providerValue) {return "";}
    const matchedUser = (appData?.users || []).find(
        (user) => user.id === providerValue,
    );
    return getUserFullDisplayName(matchedUser || {
        id: providerValue,
        name: providerValue,
    });
}

/**
 * Resolves patient provider arrays into display labels.
 * @param {Array<string>} providerValues Stored provider values.
 * @returns {Array<string>} Display labels.
 */
export function getPatientProviderDisplayNames(providerValues = []) {
    if (!Array.isArray(providerValues)) {return [];}
    return providerValues
        .filter(Boolean)
        .map((value) => getPatientProviderDisplayName(value));
}

/**
 * Returns picker options for patient providers.
 * @returns {Array<object>} Picker options with { id, name }.
 */
export function getPatientProviderPickerOptions() {
    return getAppProviderUsers()
        .map((user) => ({
        id: user.id,
        name: getUserFullDisplayName(user),
        }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

/**
 * Returns patient provider filter options using stored provider IDs.
 * @returns {Array<object>} Filter options with { value, text }.
 */
export function getPatientProviderFilterOptions() {
    const users = appData?.users || [];
    return users
        .filter((user) => user?.isAppProvider === true)
        .map((user) => ({
            value: user.id,
            text: getUserFullDisplayName(user),
        }))
        .sort((a, b) => (a.text || "").localeCompare(b.text || ""));
}
